import type { QuestInstructionsDraft } from "@/lib/questInstructions";

// ─── Agent Status ──────────────────────────────────────
export type AgentStatus =
  | "idle"
  | "planning"
  | "executing"
  | "paused_clarification"
  | "waiting_human"
  | "done"
  | "error";

// ─── Agent Run ─────────────────────────────────────────
export interface AgentRun {
  id: string;
  status: AgentStatus;
  prompt: string;
  title: string;
  model: string;
  maxIterations: number;
  maxTokens: number;
  tokensUsed: number;
  questId: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

// ─── Messages ──────────────────────────────────────────
export type MessageRole = "user" | "assistant" | "tool_result";

export interface AgentMessage {
  id: string;
  runId: string;
  role: MessageRole;
  content: ContentBlock[];
  tokenCount: number;
  timestamp: string;
}

// ─── Content Blocks (Anthropic-compatible) ─────────────
export type ContentBlock =
  | TextBlock
  | ToolUseBlock
  | ToolResultBlock;

export interface TextBlock {
  type: "text";
  text: string;
}

export interface ToolUseBlock {
  type: "tool_use";
  id: string;
  name: string;
  input: Record<string, unknown>;
}

export interface ToolResultBlock {
  type: "tool_result";
  tool_use_id: string;
  content: string;
  is_error?: boolean;
}

// ─── Tool System ───────────────────────────────────────
export interface ToolSchema {
  name: string;
  description: string;
  input_schema: {
    type: "object";
    properties: Record<string, unknown>;
    required?: string[];
  };
}

export interface ToolResult {
  content: string;
  is_error?: boolean;
}

export interface AgentTool {
  name: string;
  description: string;
  parametersSchema: ToolSchema["input_schema"];
  execute: (input: Record<string, unknown>) => Promise<ToolResult>;
}

// ─── Events (Worker → Main Thread) ─────────────────────
export type AgentEvent =
  | { type: "status_change"; status: AgentStatus }
  | { type: "text_delta"; text: string }
  | { type: "text_done"; text: string }
  | { type: "tool_call_start"; toolCallId: string; toolName: string; input: Record<string, unknown> }
  | { type: "tool_call_result"; toolCallId: string; toolName: string; result: ToolResult; durationMs: number }
  | { type: "step_start"; iteration: number }
  | { type: "step_end"; iteration: number; tokensUsed: number }
  | { type: "clarification_needed"; question: string; options?: string[] }
  | { type: "human_task_detected"; tasks: HumanTaskProposal[]; template?: { maxSlots: number }; brief?: QuestInstructionsDraft }
  | { type: "fund_wallet_requested"; reason: string; suggestedAmountUsd?: number }
  | { type: "token_warning"; tokensUsed: number; maxTokens: number }
  | { type: "run_done"; totalTokens: number; iterations: number }
  | { type: "run_error"; error: string };

// ─── Human Task Proposals ──────────────────────────────
export interface HumanTaskProposal {
  title: string;
  description: string;
  skills: string[];
  estimatedHours: number;
  budgetUsd: number;
  workMode?: "remote" | "onsite" | "hybrid";
  locationCountry?: string;
  locationCity?: string;
  /** Model output; sanitized by initialTrustFactors. */
  trustFactors?: unknown;
}

// ─── Tile Data ────────────────────────────────────────
export type TileType = "html_widget" | "human_task" | "document_viewer";

export interface TileData {
  tileId: string;
  runId: string;
  type: TileType;
  title: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Views ────────────────────────────────────────────
export type ViewMode = "tiles" | "widget";

export interface ViewData {
  viewId: string;
  runId: string;
  title: string;
  mode: ViewMode;
  tileIds: string[];
  icon: string;
  sortOrder: number;
  isSystem: boolean;
  isHidden: boolean;
  createdAt: string;
  updatedAt: string;
}

export const SYSTEM_VIEW_IDS = {
  canvas: (runId: string) => `view:canvas:${runId}`,
  files: (runId: string) => `view:files:${runId}`,
  pendingJobs: (runId: string) => `view:pending-jobs:${runId}`,
} as const;

// ─── Config ────────────────────────────────────────────
export interface AgentConfig {
  model: string;
  maxIterations: number;
  maxTokens: number;
  temperature: number;
}

export const DEFAULT_AGENT_CONFIG: AgentConfig = {
  model: "anthropic/claude-sonnet-4.6",
  maxIterations: 25,
  maxTokens: 200_000,
  temperature: 0.7,
};

// ─── LLM Request/Response (Anthropic Messages API) ────
export interface LLMRequest {
  model: string;
  max_tokens: number;
  temperature: number;
  system: string;
  messages: LLMMessage[];
  tools?: ToolSchema[];
  stream: boolean;
}

export interface LLMMessage {
  role: "user" | "assistant";
  content: string | ContentBlock[];
}

export interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
}

// ─── Worker Commands ───────────────────────────────────
export interface WorkerAPI {
  /**
   * Wire a main-thread token getter into the worker. Must be called
   * once after worker creation, before any agent run that needs auth.
   */
  setAccessTokenGetter(getter: () => Promise<string | null>): Promise<void>;

  /**
   * Scope the worker's IndexedDB access to a user. The worker has its own
   * copy of the storage module, so the main thread's scope does not reach it.
   */
  setUserId(userId: string | null): Promise<void>;

  startRun(
    runId: string,
    config: AgentConfig,
    prompt: string,
    onEvent: (event: AgentEvent) => void,
  ): Promise<void>;

  continueRun(
    runId: string,
    config: AgentConfig,
    message: string,
    onEvent: (event: AgentEvent) => void,
  ): Promise<void>;

  cancelRun(runId: string): Promise<void>;

  resumeRun(
    runId: string,
    config: AgentConfig,
    messages: LLMMessage[],
    onEvent: (event: AgentEvent) => void,
  ): Promise<void>;
}
